import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import type { UsageSeriesRow } from '../utilities/usageSeries'
import {
  type AxisSeries,
  type WindowSlot,
  drawableSeries,
  formatSeriesDay,
  isWeekend,
  periodDays,
  periodStartAt,
  scaleTicks,
  seriesAxisLabel,
  seriesByAxis,
  seriesPace,
  seriesReconciles,
  slotsPeak,
  windowDays,
  windowSlots,
} from '../utilities/usageSeries'
import { AXIS_PALETTE, formatAxisAmount, formatAxisTick } from '../utilities/usageAxis'

/**
 * Usage over time — app#146 item 3, the app half of router#1165.
 *
 * "A chart across the period rather than a single total. It answers 'when did this happen', which
 * is the first question after a surprising number."
 *
 * ⚠⚠ TWO AXES, AND STORAGE IS DELIBERATELY ABSENT — see `usageSeries.ts` and migration `0164`.
 * Storage is a LEVEL rather than a flow: it bills as a period max-of-daily-max per subject, summed
 * across subjects, so its days do not SUM to its billed figure and drawing it beside two additive
 * axes would invite a reader to add it up. It needs its own chart with its own arithmetic named
 * on it. The caption says so, rather than leaving a reader to notice the gap and distrust the rest.
 *
 * ⚠ Pure SVG, no chart dependency — the rule `UsageRings` follows (PLAN_2.6).
 *
 * ⚠ Renders nothing when no axis moved. `drawableSeries` filters on the PEAK, not on the row
 * count: the RPC returns a row for every day of the period to today including zeroes, so a length check
 * would draw a flat zero line for an organization that used nothing — which reads as a broken
 * chart rather than as a quiet month. Compute is 0.0000 fleet-wide on prod today, so the
 * bandwidth-only shape is the common one.
 *
 * ⚠⚠ THE "DAYS ADD UP" SENTENCE IS EARNED, NOT ASSERTED. `billed` carries `get_org_usage`'s figure
 * per axis — the number the bar and the ring above this chart draw — and the caption claims the
 * equality only where it holds. The two are read from different records and an aged period keeps
 * only one of them, so the claim goes false for the oldest entries in the period picker on
 * 2026-10-01; `seriesReconciles` carries the dates and the measurement. Where an axis does not
 * reconcile the difference is NAMED on that axis, the same rule `reconcileStorage` and
 * `reconcileNodes` follow: rows under a total must add up to it, and where they do not, say so
 * rather than leave a reader to find the gap and distrust the rest of the screen.
 *
 * ⚠ Measured on prod 2026-09-05: every selectable month reconciles exactly, so nothing on this
 * page says anything different today. The claim is simply no longer unconditional.
 *
 * ⚠ Compute is drawn in dollars, like every compute figure on the panel (app#327). The series is
 * cents and `formatAxisAmount` prints it as money, so no axis needs its own conversion here.
 *
 * ⚠⚠ ONE CHART FOR EACH AXIS, AND EACH HAS FOUR PARTS (Benoit, 2026-10-01 and 2026-10-02).
 * - The days are one line in their order, and the chart is always full (`windowSlots`): the period
 *   to today, and in front of it the last days of the month before, in grey.
 * - The scale on the left is one to three round amounts that follow the days drawn (`scaleTicks`),
 *   so each day's amount is read from the chart.
 * - The dotted line is one day's even share of the period's allowance (`seriesPace`), so the reader
 *   sees each day against the allowance.
 * - The bar of a Saturday or a Sunday is lighter (`isWeekend`), so the weeks are found.
 * `usageSeries.ts` has the forms that came before and why each failed.
 */
export default function UsageOverTime({
  rows,
  previous,
  period,
  billed,
  included,
}: Readonly<{
  rows: UsageSeriesRow[]
  /** The month before the period: its rows, and its name for the caption. Undefined for a past
   *  period, which is drawn alone, and for a month that holds no usage (`previousPeriod`). */
  previous?: { rows: UsageSeriesRow[]; label: string }
  /** The period on screen, `YYYY-MM`. */
  period: string
  billed?: Record<string, number | null | undefined>
  /** `get_org_usage`'s allowance for each axis, the figure the bar above the chart draws against.
   *  Null is unlimited; undefined is not loaded. The chart draws one day's share of it as a line. */
  included?: Record<string, number | null | undefined>
}>) {
  const theme = useTheme()
  const series = drawableSeries(rows)
  if (series.length === 0) return null

  const addsUp = series.every(s => seriesReconciles(s, billed?.[s.axis]))
  // The month before, by axis. ⚠ `seriesByAxis` and not `drawableSeries`: a grey day of zero is a
  // day of the chart, and an axis that did not move in that month still has its days.
  const before = new Map(seriesByAxis(previous?.rows ?? []).map(s => [s.axis, s]))
  // ⚠ One set of days for the panel — see `windowDays`. Null draws the period alone.
  const days = windowDays(series, [...before.values()], period)
  const charts = series.map(s => {
    const slots = windowSlots(s, before.get(s.axis), period, days)
    return { series: s, slots, ticks: scaleTicks(slotsPeak(slots)) }
  })
  // Every mark of every chart, as printed. Each chart sizes its scale column to the widest of them,
  // so the charts start at one left edge and a day is at one place down the panel.
  const marks = [...new Set(charts.flatMap(c => c.ticks.map(t => formatAxisTick(c.series.unit, t))))]

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Typography variant="subtitle2">Usage over time</Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
        {days && previous
          ? `The last ${days.length} days, in the unit the bars above use. The grey days are ${previous.label}.${addsUp ? ' The days in colour add up to the period total.' : ''}`
          : `Each day of this period, in the unit the bars above use.${addsUp ? ' The days add up to the period total.' : ''}`}{' '}
        Saturdays and Sundays are lighter. Storage is not shown here: it is a level rather than a
        flow, so its days do not add up.
      </Typography>
      <Stack spacing={3}>
        {charts.map(({ series: s, slots, ticks }) => (
          <AxisChart
            key={s.axis}
            series={s}
            slots={slots}
            ticks={ticks}
            // The same axis colours the rings and the bars above use, so one axis is one colour
            // down the page. One definition, in `usageAxis.ts`.
            color={theme.palette[AXIS_PALETTE[s.axis] ?? 'primary'].main}
            period={period}
            billed={billed?.[s.axis]}
            included={included?.[s.axis]}
            amount={value => formatAxisAmount(s.unit, value)}
            round={value => formatAxisTick(s.unit, value)}
            marks={marks}
          />
        ))}
      </Stack>
    </Paper>
  )
}

const HEIGHT = 96
const GAP = 2

/**
 * One axis's bars.
 *
 * ⚠ Heights are a fraction of THIS axis's own scale, never of a shared one. Bandwidth is bytes and
 * compute is cents; a shared scale would draw one of them as a flat line against the other and
 * say something false about both.
 *
 * ⚠⚠ THE SCALE IS PRINTED — see `scaleTicks`. Each mark is a round amount with a grid line, and
 * the top mark is the full height. Do not draw a chart whose height has no printed amount: that is
 * the form in which one cent of compute drew as tall as 115 GB of bandwidth.
 *
 * ⚠⚠ THE DOTTED LINE IS THE ALLOWANCE, AND IT IS NOT THE SCALE — see `seriesPace`. It is drawn
 * only when it is on the chart. When one day's share is above the top mark, the note under the
 * chart says so in words: a scale that grows to hold the line makes the days flat (app#445).
 *
 * ⚠⚠ A GREY BAR IS A DAY OF THE MONTH BEFORE — see `windowSlots`. One bar a day, the days in their
 * order. Only the days in colour are the period, and only they add up to the total on the right.
 *
 * ⚠ The bar of a Saturday or a Sunday is lighter, in colour or in grey — see `isWeekend`.
 *
 * ⚠ A day with no reading is an empty slot with no baseline; a day with no usage keeps its baseline.
 *
 * ⚠ A non-zero day is drawn at least one pixel tall. Rounding a real day to nothing would show a
 * gap where usage happened, which is the one thing this chart exists to answer.
 */
function AxisChart({
  series,
  slots,
  ticks,
  color,
  period,
  billed,
  included,
  amount,
  round,
  marks,
}: Readonly<{
  series: AxisSeries
  /** The days to draw — see `windowSlots`. */
  slots: WindowSlot[]
  /** The marks of the scale — see `scaleTicks`. The last one is the full height. */
  ticks: number[]
  color: string
  period: string
  billed?: number | null
  included?: number | null
  /** One axis amount for display, in the axis's own unit. */
  amount: (value: number) => string
  /** A round amount for display: a mark on the scale, or the allowance in a sentence. */
  round: (value: number) => string
  /** The printed marks of every chart on the panel: they give the scale column its width. */
  marks: string[]
}>) {
  const { max } = series
  const scale = ticks[ticks.length - 1]
  const pace = seriesPace(included, periodDays(period).length)
  const paceOnChart = pace !== null && pace <= scale
  const first = slots[0]
  const last = slots[slots.length - 1]
  // Where the period starts in a chart that has grey days before it — see `periodStartAt`. ⚠ Also
  // not on a phone, where the chart is about 140 px wide and three labels do not fit (measured at
  // 375 wide): the `display` of the label below.
  const startLabel = periodStartAt(slots)
  const start = slots.find(slot => slot.current)
  const grey = slots.some(slot => !slot.current)
  const reconciles = seriesReconciles(series, billed)
  /** Pixels above the bottom of the chart for one amount. */
  const rise = (value: number) => Math.round((value / scale) * HEIGHT)
  const labels = ticks.map(round)

  return (
    <Box>
      <Stack direction="row" sx={{ alignItems: 'baseline', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2">{seriesAxisLabel(series.axis)}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {amount(series.total)}
        </Typography>
      </Stack>
      {/* Two columns: the scale, then the chart. ⚠ The top margin holds the upper half of the top
          mark's label, which is centred on its line. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 1, mt: 1.5 }}>
        {/* ⚠ `aria-hidden`: the chart's own label says the total and the busiest day. */}
        <Box aria-hidden sx={{ position: 'relative', height: HEIGHT }}>
          {/* The labels are out of the flow, so these copies give the column its width: the widest
              mark of any chart on the panel. They are not shown and have no height. */}
          {marks.map(mark => (
            <Typography
              key={mark}
              variant="caption"
              sx={{ display: 'block', height: 0, visibility: 'hidden', whiteSpace: 'nowrap' }}>
              {mark}
            </Typography>
          ))}
          {ticks.map((t, i) => (
            <Typography
              key={t}
              variant="caption"
              sx={{
                position: 'absolute',
                right: 0,
                bottom: rise(t),
                transform: 'translateY(50%)',
                lineHeight: 1,
                whiteSpace: 'nowrap',
                color: 'text.secondary',
              }}>
              {labels[i]}
            </Typography>
          ))}
        </Box>
        <Box sx={{ position: 'relative', height: HEIGHT }}>
          {ticks.map(t => (
            <Box
              key={t}
              aria-hidden
              sx={{ position: 'absolute', left: 0, right: 0, bottom: rise(t), borderTop: '1px solid', borderColor: 'divider' }}
            />
          ))}
          <Stack
            direction="row"
            sx={{ position: 'relative', alignItems: 'flex-end', height: '100%', gap: `${GAP}px` }}
            role="img"
            aria-label={`${seriesAxisLabel(series.axis)} per day, ${amount(series.total)} in total, peaking at ${amount(max)}${grey && first && start ? `. The chart starts at ${formatSeriesDay(first.day)}; the days before ${formatSeriesDay(start.day)} are the month before, in grey` : ''}`}>
            {slots.map(p => {
              // A day with no reading: the slot keeps its width and draws nothing, not a baseline.
              if (p.amount === null) return <Box key={p.day} sx={{ flex: 1, minWidth: 2 }} />
              const fraction = p.amount / scale
              // ⚠ At least 1px for anything non-zero — see the note above. `Math.max` on the ROUNDED
              // value, so a day that is 0.4% of the scale is visible rather than rounded away.
              const height = p.amount > 0 ? Math.max(1, Math.round(fraction * HEIGHT)) : 0
              // A day in colour is the period; a grey day is the month before.
              const fill = p.current ? color : 'action.disabled'
              const strong = p.current ? 0.85 : 0.6
              return (
                <Box
                  key={p.day}
                  title={`${formatSeriesDay(p.day)}: ${amount(p.amount)}`}
                  sx={{
                    flex: 1,
                    minWidth: 2,
                    height: `${height}px`,
                    // ⚠ No fill on a zero day. The background paints under the border, and the border
                    // is the divider colour at 12 % opacity, so a filled zero day drew a line in the
                    // axis colour: the same mark as a non-zero day of one pixel.
                    bgcolor: height === 0 ? 'transparent' : fill,
                    // A zero day is a visible baseline rather than nothing at all, so the reader can
                    // tell "nothing happened that day" from "the chart stops here".
                    borderTop: height === 0 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    borderRadius: '2px 2px 0 0',
                    // ⚠ A Saturday or a Sunday is lighter, from the bar's own date. ⚠ ONLY A BAR. The
                    // baseline of a zero day keeps one opacity on every day, in colour or in grey:
                    // it is the divider colour at 12 %, and at a weekend's opacity it was about 4 %,
                    // which is not visible. A zero day must not read as a day with no reading.
                    opacity: height === 0 ? 0.85 : isWeekend(p.day) ? strong * 0.55 : strong,
                  }}
                />
              )
            })}
          </Stack>
          {/* One day's share of the allowance. After the bars, so it is drawn across a day that is
              above it. ⚠ `pointerEvents: none`: a bar under the line keeps its tooltip. */}
          {paceOnChart && (
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: rise(pace),
                borderTop: '2px dotted',
                borderColor: 'text.secondary',
                pointerEvents: 'none',
              }}
            />
          )}
        </Box>
        <Box />
        <Stack direction="row" sx={{ position: 'relative', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {first ? formatSeriesDay(first.day) : ''}
          </Typography>
          {startLabel !== null && (
            <Typography
              variant="caption"
              sx={{
                display: { xs: 'none', sm: 'block' },
                position: 'absolute',
                left: `${startLabel * 100}%`,
                transform: 'translateX(-50%)',
                whiteSpace: 'nowrap',
                color: 'text.secondary',
              }}>
              {start ? formatSeriesDay(start.day) : ''}
            </Typography>
          )}
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {last ? formatSeriesDay(last.day) : ''}
          </Typography>
        </Stack>
      </Box>
      {/* What the dotted line is, or why there is none. ⚠ "Included", never "limit" or "maximum":
          on a paid plan the included amount is not a ceiling (app#327). */}
      {pace !== null && included != null && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
          {paceOnChart
            ? `Dotted line: the included ${round(included)} spread over the month, ${amount(pace)} a day.`
            : `The included ${round(included)} spread over the month is ${amount(pace)} a day, above the top of this chart.`}
        </Typography>
      )}
      {/* ⚠⚠ NAME THE DIFFERENCE. The bar above this chart is read from the DETAILED records and
          this chart from the DAILY SUMMARY. The detailed ones are kept for about four months, so
          for an older period the chart has the traffic and the bar has nothing. An unexplained
          difference between two figures about the same money is worse than the larger number.
          ⚠ AND ONLY WHEN THE READER CAN SEE ONE. `seriesReconciles` compares CENTS at a 1-cent
          floor, and money prints whole cents, so two figures can fail the check and still print
          the same string — a note saying two identical figures disagree is worse than none.
          Compare the RENDERED strings before speaking. */}
      {!reconciles && billed != null && amount(billed) !== amount(series.total) && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
          The {seriesAxisLabel(series.axis).toLowerCase()} total above reads {amount(billed)}. That
          figure is measured from the detailed records, which
          are kept for about four months. This chart uses the daily summary, which is kept longer.
        </Typography>
      )}
    </Box>
  )
}
